import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { NUM, TRY, pname } from "@/lib/format";
import { getProductIndex, refreshProductIndex, searchProducts } from "@/lib/productIndex";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Loader2, Plus, Inbox, Tag, PackagePlus, CheckCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import OpenOrdersPicker from "@/components/orders/OpenOrdersPicker";

const ALL = "__all";

// Sol panel: marka filtresi + arama + çoklu seçim → forma ekle. Açık talepler ayrı listeden seçilerek aktarılır.
export default function ProductMultiPicker({ inFormIds, onAdd, openOrders = [], importedOrderIds, onImportOrders, onAddCustom }) {
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState(ALL);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [pickOpen, setPickOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/products/facets").then((r) => alive && setBrands(r.data?.brands || [])).catch(() => {});
    setLoading(true);
    getProductIndex().then((list) => { if (alive) { setIndex(list); refreshProductIndex((fresh) => alive && setIndex(fresh)); } }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const results = useMemo(() => {
    if (!index) return [];
    const f = brand !== ALL ? { brand } : {};
    if (!q.trim() && brand === ALL) return [];
    return searchProducts(index, q, f, 400);
  }, [index, q, brand]);

  const openCount = useMemo(() => openOrders.filter((o) => !importedOrderIds.has(o.id)).length, [openOrders, importedOrderIds]);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const addSelected = () => { const list = results.filter((p) => sel.has(p.id)); if (!list.length) return; onAdd(list); setSel(new Set()); };
  const addAllVisible = () => { const list = results.filter((p) => !inFormIds.has(p.id)); if (list.length) onAdd(list); setSel(new Set()); };
  const brandName = (b) => (b === ALL ? "" : b);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="of-picker">
      <OpenOrdersPicker open={pickOpen} onOpenChange={setPickOpen} orders={openOrders} initialBrand={brand === ALL ? null : brand} importedIds={importedOrderIds} onImport={onImportOrders} />
      <div className="p-3 space-y-2 border-b bg-slate-50/70">
        <div className="flex items-center gap-2">
          <Select value={brand} onValueChange={(v) => { setBrand(v); setSel(new Set()); }}>
            <SelectTrigger className="h-9 w-[46%] bg-white" data-testid="of-brand-filter"><Tag className="h-3.5 w-3.5 mr-1.5 text-primary shrink-0" /><SelectValue placeholder="Marka" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value={ALL}>Tüm Markalar</SelectItem>
              {brands.map((b) => <SelectItem key={b} value={b} data-testid={`of-brand-${b}`}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={brand === ALL ? "Ürün adı / kod / barkod ara…" : `${brand} içinde ara…`} className="pl-8 h-9 bg-white" data-testid="of-search" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" onClick={() => setPickOpen(true)} disabled={!openOrders.length} data-testid="of-import-orders">
            <Inbox className="h-3.5 w-3.5 mr-1" />Açık Talepler ({openCount})
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => onAddCustom(q.trim(), brandName(brand))} data-testid="of-add-custom">
            <PackagePlus className="h-3.5 w-3.5 mr-1" />Stokta Olmayan Ürün Ekle
          </Button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto" data-testid="of-results">
        {loading && !index ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" />Ürün dizini yükleniyor…</div>
        ) : results.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {q.trim() || brand !== ALL ? "Eşleşen ürün yok." : "Marka seçin veya ürün arayın; işaretleyip forma ekleyin."}
          </div>
        ) : results.map((p) => {
          const inForm = inFormIds.has(p.id);
          return (
            <label key={p.id} className={cn("flex items-center gap-2.5 px-3 py-2 border-b last:border-0 cursor-pointer hover:bg-muted/60 transition-colors", inForm && "bg-emerald-50/60")} data-testid={`of-result-${p.id}`}>
              <Checkbox checked={sel.has(p.id)} onCheckedChange={() => toggle(p.id)} disabled={inForm} data-testid={`of-check-${p.id}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold leading-snug break-words" title={pname(p)}>{pname(p)}</div>
                <div className="text-[10.5px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                  {p.code && <span className="font-mono">{p.code}</span>}
                  <span>Stok: <b className={cn("tabular-nums", Number(p.quantity) > 0 ? "text-foreground" : "text-destructive")}>{NUM(p.quantity)} {p.unit}</b></span>
                  {p.price > 0 && <span className="tabular-nums">Liste: {TRY(p.price)}</span>}
                </div>
              </div>
              {inForm ? <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-0.5"><CheckCheck className="h-3 w-3" />Formda</span>
                : <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={(e) => { e.preventDefault(); onAdd([p]); }} title="Hemen ekle" data-testid={`of-quick-add-${p.id}`}><Plus className="h-4 w-4" /></Button>}
            </label>
          );
        })}
      </div>
      {results.length > 0 && (
        <div className="p-2.5 border-t bg-slate-50/70 flex items-center gap-2">
          <Button size="sm" className="flex-1" disabled={!sel.size} onClick={addSelected} data-testid="of-add-selected"><Plus className="h-4 w-4 mr-1" />Seçilenleri Ekle ({sel.size})</Button>
          <Button size="sm" variant="outline" onClick={addAllVisible} data-testid="of-add-all">Tümünü Ekle ({results.filter((p) => !inFormIds.has(p.id)).length})</Button>
        </div>
      )}
    </div>
  );
}
